<div id="root">
  <div class="fixed-background">
    <div class="container document">
      <div class="row">
        <div class="col-12 col-md-12">
          <div class="card">
            <app-legal-lang-toggle [lang]="lang" (langChange)="onLangChange($event)"></app-legal-lang-toggle>
            @if (lang === 'en') {
            <h1 class="mt-4" style="text-align: center;">Privacy Policy</h1>
            <p><i>Last updated: August 18, 2026</i></p>
            <p>This Privacy Policy describes our policies and procedures on the collection, use and disclosure of your
              information when you use the service and tells you about your privacy rights and how the law protects
              you.</p>
            <p>We use your personal data to provide and improve the service. By using the service, you agree to the
              collection and use of information in accordance with this Privacy Policy.</p>
            <h2>Scope of this Policy</h2>
            <p>This single policy covers both of our products, operated by the same company on shared infrastructure:</p>
            <ul>
              <li><strong>BoothPilot</strong> — the web application at <a href="https://boothpilot.com">boothpilot.com</a>,
                used to schedule, record and broadcast events.</li>
              <li><strong>BoothPilot Replay</strong> — the companion mobile application for capturing and reviewing
                sideline clips.</li>
            </ul>
            <p>The sections below apply to both products unless stated otherwise. Where a product collects or handles
              data differently, the difference is set out under
              <a [routerLink]="[]" fragment="product-disclosures" queryParamsHandling="preserve">Product-Specific
                Disclosures</a> at the end of this policy:
              <a [routerLink]="[]" fragment="boothpilot" queryParamsHandling="preserve">BoothPilot (web)</a> and
              <a [routerLink]="[]" fragment="replay" queryParamsHandling="preserve">BoothPilot Replay (mobile)</a>.</p>
            <h2>Interpretation and Definitions</h2>
            <h3>Interpretation</h3>
            <p>The following definitions shall have the same meaning regardless of whether they appear in
              singular or in plural.</p>
            <h3>Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            <ul>
              <li>
                <strong>Account</strong> means a unique account created for you to access our Service or parts of
                  our Service.
              </li>
              <li>
                <strong>Company</strong> (referred to as either &quot;the Company&quot;, &quot;We&quot;,
                  &quot;Us&quot; or &quot;Our&quot; in this Agreement) refers to Laurent Chicoine Productions, Quebec, Canada.
              </li>
              <li>
                <strong>Cookies</strong> are small files that are placed on your computer, mobile device or any
                  other device by a website, containing the details of your browsing history on that website among its
                  many uses.
              </li>
              <li>
                <strong>Device</strong> means any device that can access the Service such as a computer, a
                  cellphone or a digital tablet.
              </li>
              <li>
                <strong>Personal Data</strong> is any information that relates to an identified or identifiable
                  individual.
              </li>
              <li>
                <strong>Application</strong> means BoothPilot Replay, the mobile software program provided by the
                  Company and downloaded by you on any Device.
              </li>
              <li>
                <strong>Service</strong> refers collectively to the Website and the Application.
              </li>
              <li>
                <strong>Service Provider</strong> means any natural or legal person who processes the data on
                  behalf of the Company. It refers to third-party companies or individuals employed by the Company to
                  facilitate the Service, to provide the Service on behalf of the Company, to perform services related
                  to the Service or to assist the Company in analyzing how the Service is used.
              </li>
              <li>
                <strong>Usage Data</strong> refers to data collected automatically, either generated by the use of
                  the Service or from the Service infrastructure itself (for example, the duration of a page visit).
              </li>
              <li>
                <strong>Website</strong> refers to BoothPilot, accessible from <a href="https://boothpilot.com"
                    rel="external nofollow noopener" target="_blank">https://boothpilot.com</a>
              </li>
              <li>
                <strong>You</strong> means the individual accessing or using the Service, or the company, or other
                  legal entity on behalf of which such individual is accessing or using the Service, as applicable.
              </li>
            </ul>
            <h2>Collecting and Using your Personal Data</h2>
            <h3>Types of Data Collected</h3>
            <h4>Personal Data</h4>
            <p>While using our Service, We may ask you to provide us with certain personally identifiable information
              that can be used to contact or identify you. Personally identifiable information may include, but is not
              limited to:</p>
            <ul>
              <li>
                Email address
              </li>
              <li>
                First name and last name
              </li>
              <li>
                Usage Data
              </li>
            </ul>
            <h4>Usage Data</h4>
            <p><em>This section applies to the Website only. BoothPilot Replay, the mobile Application, does not
              collect Usage Data: it does not record your IP address, does not use cookies or similar tracking
              technologies, and contains no analytics, advertising, attribution or crash-reporting software.</em></p>
            <p>Usage Data is collected automatically when using the Website.</p>
            <p>Usage Data may include information such as your Device's Internet Protocol address (e.g. IP address),
              browser type, browser version, the pages of our Service that you visit, the time and date of your visit,
              the time spent on those pages, unique device identifiers and other diagnostic data.</p>
            <p>We may also collect information that your browser sends whenever you visit the Website.</p>
            <h4>Tracking Technologies and Cookies</h4>
            <p><em>This section applies to the Website only. The mobile Application uses no cookies, web beacons or
              similar tracking technologies.</em></p>
            <p>We use Cookies and similar tracking technologies on the Website to track activity and store certain
              information. Tracking technologies used are beacons, tags, and scripts to collect and track information
              and to improve and analyze our Service. The technologies We use may include:</p>
            <ul>
              <li><strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on your Device. You can
                instruct your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if
                you do not accept Cookies, you may not be able to use some parts of our Service. Unless you have
                adjusted your browser setting so that it will refuse Cookies, our Service may use Cookies.</li>
              <li><strong>Web Beacons.</strong> Certain sections of our Service and our emails may contain small
                electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel
                gifs) that permit the Company, for example, to count users who have visited those pages or opened an
                email and for other related website statistics (for example, recording the popularity of a certain
                section and verifying system and server integrity).</li>
            </ul>
            <p>Cookies can be &quot;Persistent&quot; or &quot;Session&quot; Cookies. Persistent Cookies remain on your
              personal computer or mobile device when you go offline, while Session Cookies are deleted as soon as you
              close your web browser.</p>
            <p>We use both Session and Persistent Cookies for the purposes set out below:</p>
            <ul>
              <li>
                <strong>Necessary / Essential Cookies</strong><br/>
                Type: Session Cookies<br/>
                Administered by: Us<br/>
                Purpose: These Cookies are essential to provide you with services available through the Website and
                  to enable you to use some of its features. They help to authenticate users and prevent fraudulent
                  use of user accounts. Without these Cookies, the services that you have asked for cannot be
                  provided, and We only use these Cookies to provide you with those services.
              </li>
              <li>
                <strong>Functionality Cookies</strong><br/>
                Type: Persistent Cookies<br/>
                Administered by: Us<br/>
                Purpose: These Cookies allow us to remember choices you make when you use the Website, such as
                  remembering your login details or language preference. The purpose of these Cookies is to provide
                  you with a more personal experience and to avoid you having to re-enter your preferences every time
                  you use the Website.
              </li>
            </ul>
            <h3>Use of your Personal Data</h3>
            <p>The Company may use Personal Data for the following purposes:</p>
            <ul>
              <li>
                <strong>To provide and maintain our Service</strong>, including to monitor the usage of our
                  Service.
              </li>
              <li>
                <strong>To manage your Account:</strong> to manage your registration as a user of the Service. The
                  Personal Data you provide can give you access to different functionalities of the Service that are
                  available to you as a registered user.
              </li>
              <li>
                <strong>For the performance of a contract:</strong> the development, compliance and undertaking of
                  the purchase contract for the products, items or services you have purchased or of any other
                  contract with us through the Service.
              </li>
              <li>
                <strong>To contact you:</strong> To contact you by email, telephone calls, SMS, or other equivalent
                  forms of electronic communication, such as a mobile application's push notifications regarding
                  updates or informative communications related to the functionalities, products or contracted
                  services, including the security updates, when necessary or reasonable for their implementation.
              </li>
              <li>
                <strong>To provide you</strong> with news, special offers and general information about other
                  goods, services and events which we offer that are similar to those that you have already purchased
                  or enquired about unless you have opted not to receive such information.
              </li>
              <li>
                <strong>To manage your requests:</strong> To attend and manage your requests to us.
              </li>
              <li>
                <strong>For business transfers:</strong> We may use your information to evaluate or conduct a
                  merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some
                  or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar
                  proceeding, in which Personal Data held by Us about our Service users is among the assets
                  transferred.
              </li>
              <li>
                <strong>For other purposes</strong>: We may use your information for other purposes, such as data
                  analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and
                  to evaluate and improve our Service, products, services, marketing and your experience.
              </li>
            </ul>
            <p>We may share your personal information in the following situations:</p>
            <ul>
              <li><strong>With Service Providers:</strong> We may share your personal information with Service
                Providers to monitor and analyze the use of our Service, to contact you.</li>
              <li><strong>For business transfers:</strong> We may share or transfer your personal information in
                connection with, or during negotiations of, any merger, sale of Company assets, financing, or
                acquisition of all or a portion of Our business to another company.</li>
              <li><strong>With affiliates:</strong> We may share your information with our affiliates, in which case
                we will require those affiliates to honor this Privacy Policy. Affiliates include our parent company
                and any other subsidiaries, joint venture partners or other companies that we control or that are
                under common control with us.</li>
              <li><strong>With other users:</strong> Administrators of your organization will have access to some of your personal data to manage your access to the Service.</li>
              <li><strong>With your consent</strong>: We may disclose your personal information for any other purpose
                with your consent.</li>
            </ul>
            <h3>Retention of your Personal Data</h3>
            <p>The Company will retain your Personal Data only for as long as is necessary for the purposes set out in
              this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with
              our legal obligations (for example, if we are required to retain your data to comply with applicable
              laws), resolve disputes, and enforce our legal agreements and policies.</p>
            <p>In practice, that means the following:</p>
            <ul>
              <li><strong>Account information</strong> — such as your name, email address and the teams you belong to
                — is retained for as long as your account exists. Deleting your account removes it, except where we
                are required to keep some of it to meet a legal obligation.</li>
              <li><strong>Clips recorded with BoothPilot Replay</strong> are not held by us. They are stored in the
                Google Drive account linked by your team administrator and are retained for as long as your team
                keeps them there. The information we hold about those clips — the event, the tags and which member
                recorded them — is retained until the clip or the account is deleted.</li>
              <li><strong>Recordings delivered to an external video tool</strong> you have connected, such as your
                YouTube channel or Hudl, are kept by us for no more than <strong>7 days</strong> after delivery. What
                that external tool retains afterwards is governed by your arrangements with it.</li>
              <li><strong>Recordings hosted by us</strong> for playback through the Service are retained for
                <strong>180 days</strong> by default. A different retention period may be agreed with your team in
                writing, in which case that period applies instead.</li>
            </ul>
            <p>Backups may hold deleted content for a short period afterwards before expiring in the ordinary
              course.</p>
            <p>The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally
              retained for a shorter period of time, except when this data is used to strengthen the security or to
              improve the functionality of our Service, or we are legally obligated to retain this data for longer
              time periods.</p>
            <h3>Transfer of your Personal Data</h3>
            <p>Your information, including Personal Data, is processed at the Company's operating offices and in any
              other places where the parties involved in the processing are located. We use third-party vendors to provide
              the necessary technology required to run the Service, and such third-party vendors may collect and store your data on our behalf.
              It means that this information may be transferred to — and maintained on — computers located outside of your state, province, 
              country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.</p>
            <p>Your consent to this Privacy Policy followed by your submission of such information represents your
              agreement to that transfer.</p>
            <p>The Company will take all steps reasonably necessary to ensure that your data is treated securely and
              in accordance with this Privacy Policy and no transfer of your Personal Data will take place to an
              organization or a country unless there are adequate controls in place including the security of your
              data and other personal information.</p>
            <h3>Disclosure of your Personal Data</h3>
            <h4>Business Transactions</h4>
            <p>If the Company is involved in a merger, acquisition or asset sale, your Personal Data may be
              transferred. We will provide notice before your Personal Data is transferred and becomes subject to a
              different Privacy Policy.</p>
            <h4>Law enforcement</h4>
            <p>Under certain circumstances, the Company may be required to disclose your Personal Data if required to
              do so by law or in response to valid requests by public authorities (e.g. a court or a government
              agency).</p>
            <h4>Other legal requirements</h4>
            <p>The Company may disclose your Personal Data in the good faith belief that such action is necessary to:
            </p>
            <ul>
              <li>Comply with a legal obligation</li>
              <li>Protect and defend the rights or property of the Company</li>
              <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
              <li>Protect the personal safety of Users of the Service or the public</li>
              <li>Protect against legal liability</li>
            </ul>
            <h3>Security of your Personal Data</h3>
            <p>The security of your Personal Data is important to Us, but remember that no method of transmission over
              the Internet, or method of electronic storage is 100% secure. While we strive to use commercially
              acceptable means to protect your Personal Data, we cannot guarantee its absolute security.</p>
            <h2>Children's Privacy</h2>
            <p>Our Service does not address anyone under the age of 13. We do not knowingly collect personally
              identifiable information from anyone under the age of 13. If you are a parent or guardian and you are
              aware that your child has provided us with Personal Data, please contact us. If We become aware that we
              have collected Personal Data from anyone under the age of 13 without verification of parental consent,
              we take steps to remove that information.</p>
            <h2>Third Party Services</h2>
            <p>Data collected on client's site is managed and controlled by the client (Operator). Our company is only the processor of the data collected.
              Operators are responsible to inform players, coaches or any other persons and if necessary, to obtain their consent to be recorded and to stream the live video on the internet.
            </p>
            <p>
              Our Service does not serve any advertisements neither on the Website or on content broadcasted to third party services.
            </p>
            <p>Our Service contains links and integrations to other websites that are not operated by us. If you click on a third
              party link, you will be directed to that third party's site. We strongly advise you to review the
              Privacy Policy of every site you visit.</p>
            <p>Both products run on shared infrastructure provided by:</p>
            <ul>
              <li><strong>Firebase (Google)</strong> — authentication, database and cloud functions. See the
                <a href="https://firebase.google.com/support/privacy" target="_blank">Firebase Privacy Policy</a>.</li>
            </ul>
            <p>Each product also has its own optional integrations, listed under
              <a [routerLink]="[]" fragment="product-disclosures" queryParamsHandling="preserve">Product-Specific
                Disclosures</a> below.</p>
            <p>We have no control over and assume no responsibility for the content, privacy policies or practices of
              any third party sites or services.</p>
            <h2 id="product-disclosures">Product-Specific Disclosures</h2>
            <p>The following sections describe handling that is specific to one product. Everything above continues to
              apply.</p>

            <h3 id="boothpilot">BoothPilot (web)</h3>
            <p>The integrations below are optional and apply to the web application only. They are not available in,
              and are not used by, BoothPilot Replay.</p>

            <h4>YouTube</h4>
            <p>If you choose to connect a YouTube channel, we request the
              <strong>https://www.googleapis.com/auth/youtube.force-ssl</strong> scope. We use it solely to create and
              manage the live broadcasts you schedule in the Service — to create broadcasts and streams, bind them to
              your channel, and read back their status.</p>
            <p>We store the resulting access and refresh tokens, the public links to the broadcasts, and the stream
              keys. This data is held securely with access control rules in place, and is refreshed for validity and
              authorization at least every 7 days.</p>
            <p>When you use this integration you agree to the
              <a href="https://www.youtube.com/t/terms" target="_blank">YouTube Terms of Service</a> and the
              <a href="http://www.google.com/policies/privacy" target="_blank">Google Privacy Policy</a>.</p>
            <p>You can revoke this access at any time using the Revoke button in the application or through the Google
              security settings page at
              <a href="https://myaccount.google.com/permissions" target="_blank">https://myaccount.google.com/permissions</a>.
              When access is revoked, all data associated with your YouTube account is deleted from the application.</p>

            <h4>Hudl</h4>
            <p>Captured events can be uploaded to your Hudl team. You can review the
              <a href="https://www.hudl.com/privacy" target="_blank">Hudl Privacy Policy</a>.</p>

            <h3 id="replay">BoothPilot Replay (mobile)</h3>
            <h4>Account and team data</h4>
            <p>When you create an account we collect your email address, your display name, and data from your
              authentication provider if you sign in with Google or with Apple rather than with an email and
              password. As you use the
              Application we record your team memberships and your role within each team, event metadata such
              as names, dates and tags, and clip metadata such as timestamps, play tags and file references.</p>

            <h4>Video and image data</h4>
            <p><strong>We do not store your video recordings or thumbnail images on our servers.</strong> Video files
              and thumbnails are uploaded directly from your Device to your team's linked Google Drive account. We store
              only the Google Drive file identifiers in our database, so that playback and synchronization work. The
              media files themselves remain in the Google Drive account controlled by your team administrator.</p>
            <p>Local copies of videos may be cached on your Device for offline playback. These are removed when you
              uninstall the Application.</p>

            <h4>Google Drive access and Limited Use</h4>
            <p>To store your team's footage, a team administrator links a Google Drive account. We request a single
              scope:</p>
            <ul>
              <li><strong>https://www.googleapis.com/auth/drive.file</strong> — permits the Application to create files
                in Google Drive and to access only those files it has created. It grants no access to any other file in
                your Drive. We use it to upload clips and thumbnails, to read them back for playback, and to delete them
                when you delete a clip in the Application.</li>
            </ul>
            <p>Our use and transfer of information received from Google APIs adheres to the
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank">Google API
                Services User Data Policy</a>, including the Limited Use requirements. Specifically, data obtained
              through Google APIs is used only to provide and improve the user-facing features described above; it is
              not used for advertising of any kind; it is not sold or transferred to third parties except as necessary
              to provide the Service, to comply with applicable law, or as part of a merger or acquisition; and it is
              not read by humans except with your explicit consent, to resolve a support request, for security purposes
              such as investigating abuse, or where required by law.</p>
            <p>A team administrator can disconnect the linked Drive account at any time from the team settings screen,
              and you can revoke the Application's access from
              <a href="https://myaccount.google.com/permissions" target="_blank">https://myaccount.google.com/permissions</a>.</p>

            <h4>Support requests and feedback</h4>
            <p>If you send us a support request or feedback from within the Application, we store the message you
              write together with your name, your email address, the team you were using at the time and whether you
              were on iOS or Android, so that we can reply to you and reproduce the problem. Support requests and
              feedback are kept separately from your team's game data and are used only to answer you and to improve
              the Application.</p>
            <p>The Application also keeps a short technical log of recent uploads on your Device. This log is never
              sent automatically. It is shared only if you choose to share it, using your Device's own sharing
              options, and you decide where it goes. Access tokens, web addresses and file paths are removed from it
              before it is shown to you.</p>

            <h4>Device identifier</h4>
            <p>The Application generates a random identifier for each installation and stores it on your Device. It is
              recorded, together with a device name you can change, when your device holds a recording or data-entry
              control, so that everyone on your team can see which device is currently recording. It is not derived
              from any hardware or advertising identifier, it is not shared with anyone, and it is not used to track
              you. It is removed when you uninstall the Application.</p>

            <h4>Device permissions</h4>
            <p>The Application requests access to your <strong>camera</strong>, to record footage, and to your
              <strong>microphone</strong>, to capture audio with those recordings. Both are requested only when the
              feature is used and can be revoked at any time in your device settings.</p>

            <h4>Data residency</h4>
            <p>When you create a team you choose a data region, currently the United States or Canada. Your team's data
              is stored in that region's database. Account-level data is stored in the United States.</p>

            <h4>Deleting your account</h4>
            <p>You may delete your account at any time from within the Application. Doing so removes you from all teams,
              deletes your user profile from all regional databases, and deletes your authentication account. Video
              files held in Google Drive are not affected, because they are controlled by your team's Drive account
              rather than by us.</p>

            <h4>Optional integrations</h4>
            <p>In addition to the shared Firebase infrastructure described above, the Application uses:</p>
            <ul>
              <li><strong>Google Drive API</strong> — video and thumbnail storage, as described above. See the
                <a href="https://policies.google.com/privacy" target="_blank">Google Privacy Policy</a>.</li>
              <li><strong>Google Sign-In</strong> — optional authentication. When you use it we receive your name,
                email address and profile picture from your Google account, and nothing else; we do not request access
                to any other Google service through it. See the
                <a href="https://policies.google.com/privacy" target="_blank">Google Privacy Policy</a>.</li>
              <li><strong>Apple Sign-In</strong> — optional authentication on iOS devices. See the
                <a href="https://www.apple.com/legal/privacy/" target="_blank">Apple Privacy Policy</a>.</li>
            </ul>

            <h2>Changes to this Privacy Policy</h2>
            <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the
              new Privacy Policy on this page.</p>
            <p>We will let you know via email and/or a prominent notice on our Service, prior to the change becoming
              effective and update the &quot;Last updated&quot; date at the top of this Privacy Policy.</p>
            <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy
              Policy are effective when they are posted on this page.</p>
            <h2>Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our privacy practices, you can contact us:</p>
            <ul>
              <li>By email: hostmaster&#64;boothpilot.com</li>
            </ul>
            } @else {
            <h1 class="mt-4" style="text-align: center;">Politique de confidentialité</h1>
            <p><i>Dernière mise à jour : 18 août 2026</i></p>
            <p>La présente politique de confidentialité décrit nos pratiques en matière de collecte,
              d'utilisation et de divulgation de vos renseignements lorsque vous utilisez le Service, et vous
              informe de vos droits en matière de vie privée et de la protection que la loi vous accorde.</p>
            <p>Nous utilisons vos données personnelles pour fournir et améliorer le Service. En utilisant le
              Service, vous consentez à la collecte et à l'utilisation de renseignements conformément à la
              présente politique de confidentialité.</p>
            <h2>Portée de la présente politique</h2>
            <p>Cette politique unique couvre nos deux produits, exploités par la même société sur une
              infrastructure commune :</p>
            <ul>
              <li><strong>BoothPilot</strong> — l'application Web accessible à
                <a href="https://boothpilot.com">boothpilot.com</a>, utilisée pour planifier, enregistrer et
                diffuser des événements.</li>
              <li><strong>BoothPilot Replay</strong> — l'application mobile complémentaire servant à capter et à
                revoir des séquences en bordure du terrain.</li>
            </ul>
            <p>Les sections ci-dessous s'appliquent aux deux produits, sauf indication contraire. Lorsqu'un
              produit recueille ou traite des données différemment, la différence est précisée sous
              <a [routerLink]="[]" fragment="product-disclosures-fr" queryParamsHandling="preserve">Renseignements
                propres à chaque produit</a>, à la fin de la présente politique :
              <a [routerLink]="[]" fragment="boothpilot-fr" queryParamsHandling="preserve">BoothPilot (Web)</a> et
              <a [routerLink]="[]" fragment="replay-fr" queryParamsHandling="preserve">BoothPilot Replay (mobile)</a>.</p>

            <h2>Interprétation et définitions</h2>
            <h3>Interprétation</h3>
            <p>Les définitions qui suivent ont la même signification, qu'elles apparaissent au singulier ou au
              pluriel.</p>
            <h3>Définitions</h3>
            <p>Aux fins de la présente politique de confidentialité :</p>
            <ul>
              <li>
                <strong>Compte</strong> désigne un compte unique créé pour vous donner accès au Service ou à
                  certaines de ses parties.
              </li>
              <li>
                <strong>Société</strong> (désignée par « la Société », « nous » ou « notre » dans la présente
                  entente) désigne Laurent Chicoine Productions.
              </li>
              <li>
                <strong>Témoins (cookies)</strong> désigne les petits fichiers déposés sur votre ordinateur,
                  votre appareil mobile ou tout autre appareil par un site Web, contenant notamment
                  l'historique de votre navigation sur ce site.
              </li>
              <li>
                <strong>Appareil</strong> désigne tout appareil pouvant accéder au Service, tel qu'un
                  ordinateur, un téléphone cellulaire ou une tablette.
              </li>
              <li>
                <strong>Données personnelles</strong> désigne tout renseignement se rapportant à une personne
                  physique identifiée ou identifiable.
              </li>
              <li>
                <strong>Application</strong> désigne BoothPilot Replay, le logiciel mobile fourni par la
                  Société et téléchargé par vous sur un Appareil.
              </li>
              <li>
                <strong>Service</strong> désigne collectivement le Site Web et l'Application.
              </li>
              <li>
                <strong>Fournisseur de services</strong> désigne toute personne physique ou morale qui traite
                  les données pour le compte de la Société. Il s'agit de sociétés tierces ou de personnes
                  employées par la Société pour faciliter le Service, le fournir en son nom, exécuter des
                  services qui s'y rattachent ou l'aider à analyser l'utilisation qui en est faite.
              </li>
              <li>
                <strong>Données d'utilisation</strong> désigne les données recueillies automatiquement, soit
                  générées par l'utilisation du Service, soit provenant de l'infrastructure du Service
                  elle-même (par exemple, la durée de consultation d'une page).
              </li>
              <li>
                <strong>Site Web</strong> désigne BoothPilot, accessible à <a href="https://boothpilot.com"
                    rel="external nofollow noopener" target="_blank">https://boothpilot.com</a>
              </li>
              <li>
                <strong>Vous</strong> désigne la personne qui accède au Service ou l'utilise, ou la société ou
                  autre entité juridique au nom de laquelle cette personne accède au Service ou l'utilise,
                  selon le cas.
              </li>
            </ul>

            <h2>Collecte et utilisation de vos données personnelles</h2>
            <h3>Types de données recueillies</h3>
            <h4>Données personnelles</h4>
            <p>Dans le cadre de l'utilisation du Service, nous pouvons vous demander de nous fournir certains
              renseignements permettant de vous identifier ou de communiquer avec vous. Ces renseignements
              peuvent comprendre, sans s'y limiter :</p>
            <ul>
              <li>l'adresse courriel;</li>
              <li>le prénom et le nom;</li>
              <li>les données d'utilisation.</li>
            </ul>
            <h4>Données d'utilisation</h4>
            <p><em>Cette section vise uniquement le Site Web. BoothPilot Replay, l'Application mobile, ne recueille
              aucune donnée d'utilisation : elle n'enregistre pas votre adresse IP, n'utilise ni témoins ni
              technologies de suivi semblables, et ne contient aucun logiciel d'analyse, de publicité,
              d'attribution ou de rapport de plantage.</em></p>
            <p>Les données d'utilisation sont recueillies automatiquement lors de l'utilisation du Site Web.</p>
            <p>Elles peuvent comprendre des renseignements tels que l'adresse de protocole Internet (adresse
              IP) de votre Appareil, le type et la version de votre navigateur, les pages du Service que vous
              consultez, la date et l'heure de votre visite, le temps passé sur ces pages, les identifiants
              uniques d'appareil et d'autres données de diagnostic.</p>
            <p>Nous pouvons également recueillir les renseignements que votre navigateur transmet chaque fois
              que vous visitez le Site Web.</p>
            <h4>Technologies de suivi et témoins</h4>
            <p><em>Cette section vise uniquement le Site Web. L'Application mobile n'utilise aucun témoin, balise
              Web ou technologie de suivi semblable.</em></p>
            <p>Nous utilisons des témoins et des technologies de suivi semblables pour suivre l'activité sur
              le Site Web et conserver certains renseignements. Les technologies employées comprennent des
              balises, des marqueurs et des scripts servant à recueillir et à suivre des renseignements ainsi
              qu'à améliorer et à analyser notre Service :</p>
            <ul>
              <li><strong>Témoins de navigateur.</strong> Un témoin est un petit fichier déposé sur votre
                Appareil. Vous pouvez configurer votre navigateur pour refuser tous les témoins ou pour vous
                signaler leur envoi. Toutefois, si vous refusez les témoins, certaines parties du Service
                pourraient vous être inaccessibles. À moins que vous n'ayez configuré votre navigateur pour
                les refuser, notre Service peut utiliser des témoins.</li>
              <li><strong>Balises Web.</strong> Certaines sections de notre Service et de nos courriels peuvent
                contenir de petits fichiers électroniques appelés balises Web (ou pixels invisibles) qui
                permettent à la Société, par exemple, de compter les personnes ayant consulté ces pages ou
                ouvert un courriel, et d'établir d'autres statistiques (par exemple, la popularité d'une
                section ou la vérification de l'intégrité du système et des serveurs).</li>
            </ul>
            <p>Les témoins peuvent être « persistants » ou « de session ». Les témoins persistants demeurent sur
              votre ordinateur ou votre appareil mobile lorsque vous vous déconnectez, tandis que les témoins
              de session sont supprimés dès la fermeture de votre navigateur.</p>
            <p>Nous utilisons des témoins de session et des témoins persistants aux fins suivantes :</p>
            <ul>
              <li>
                <strong>Témoins nécessaires ou essentiels</strong><br/>
                Type : témoins de session<br/>
                Administrés par : nous<br/>
                Finalité : ces témoins sont essentiels pour vous fournir les services offerts par le Site Web
                  et vous permettre d'en utiliser certaines fonctions. Ils servent à authentifier les
                  utilisateurs et à prévenir l'utilisation frauduleuse des comptes. Sans eux, les services que
                  vous avez demandés ne peuvent être fournis, et nous ne les utilisons qu'à cette fin.
              </li>
              <li>
                <strong>Témoins de fonctionnalité</strong><br/>
                Type : témoins persistants<br/>
                Administrés par : nous<br/>
                Finalité : ces témoins nous permettent de mémoriser les choix que vous faites lorsque vous
                  utilisez le Site Web, comme vos identifiants de connexion ou votre préférence de langue.
                  Ils visent à vous offrir une expérience plus personnalisée et à vous éviter d'avoir à saisir
                  de nouveau vos préférences à chaque visite.
              </li>
            </ul>

            <h3>Utilisation de vos données personnelles</h3>
            <p>La Société peut utiliser les données personnelles aux fins suivantes :</p>
            <ul>
              <li><strong>Fournir et maintenir notre Service</strong>, y compris en surveiller l'utilisation.</li>
              <li><strong>Gérer votre Compte :</strong> gérer votre inscription à titre d'utilisateur du
                Service. Les données personnelles que vous fournissez peuvent vous donner accès à différentes
                fonctionnalités du Service offertes aux utilisateurs inscrits.</li>
              <li><strong>Exécuter un contrat :</strong> élaborer, respecter et exécuter le contrat d'achat des
                produits, articles ou services que vous avez achetés, ou tout autre contrat conclu avec nous
                par l'entremise du Service.</li>
              <li><strong>Communiquer avec vous :</strong> vous joindre par courriel, par téléphone, par
                message texte ou par toute autre forme de communication électronique équivalente, telle que
                les notifications poussées d'une application mobile, concernant des mises à jour ou des
                communications informatives relatives aux fonctionnalités, aux produits ou aux services
                souscrits, y compris les mises à jour de sécurité, lorsque cela est nécessaire ou raisonnable
                pour leur mise en œuvre.</li>
              <li><strong>Vous transmettre</strong> des nouvelles, des offres spéciales et des renseignements
                généraux sur d'autres biens, services et événements que nous offrons et qui sont semblables à
                ceux que vous avez déjà achetés ou au sujet desquels vous vous êtes renseigné, sauf si vous
                avez choisi de ne pas recevoir ces renseignements.</li>
              <li><strong>Traiter vos demandes :</strong> répondre à vos demandes et en assurer le suivi.</li>
              <li><strong>Réaliser un transfert d'entreprise :</strong> nous pouvons utiliser vos
                renseignements pour évaluer ou réaliser une fusion, une cession, une restructuration, une
                réorganisation, une dissolution ou toute autre vente ou transfert de la totalité ou d'une
                partie de nos actifs, que ce soit dans le cadre de la poursuite des activités ou d'une
                procédure de faillite, de liquidation ou de nature semblable, lorsque les données personnelles
                que nous détenons au sujet des utilisateurs du Service font partie des actifs transférés.</li>
              <li><strong>À d'autres fins :</strong> nous pouvons utiliser vos renseignements à d'autres fins,
                notamment l'analyse de données, la détermination des tendances d'utilisation, l'évaluation de
                l'efficacité de nos campagnes promotionnelles ainsi que l'évaluation et l'amélioration de
                notre Service, de nos produits, de notre marketing et de votre expérience.</li>
            </ul>
            <p>Nous pouvons communiquer vos renseignements personnels dans les situations suivantes :</p>
            <ul>
              <li><strong>Avec des fournisseurs de services :</strong> pour surveiller et analyser
                l'utilisation de notre Service et pour communiquer avec vous.</li>
              <li><strong>Dans le cadre d'un transfert d'entreprise :</strong> nous pouvons communiquer ou
                transférer vos renseignements personnels dans le cadre de négociations relatives à une fusion,
                à une vente d'actifs de la Société, à un financement ou à l'acquisition de la totalité ou d'une
                partie de nos activités par une autre société.</li>
              <li><strong>Avec des sociétés affiliées :</strong> nous pouvons communiquer vos renseignements à
                nos sociétés affiliées, auquel cas nous exigerons qu'elles respectent la présente politique de
                confidentialité. Les sociétés affiliées comprennent notre société mère et toute autre filiale,
                partenaire de coentreprise ou société que nous contrôlons ou qui est sous contrôle commun avec
                nous.</li>
              <li><strong>Avec d'autres utilisateurs :</strong> les administrateurs de votre organisation
                auront accès à certaines de vos données personnelles afin de gérer votre accès au Service.</li>
              <li><strong>Avec votre consentement :</strong> nous pouvons communiquer vos renseignements
                personnels à toute autre fin avec votre consentement.</li>
            </ul>

            <h3>Conservation de vos données personnelles</h3>
            <p>La Société ne conservera vos données personnelles que le temps nécessaire aux fins énoncées dans
              la présente politique. Nous conserverons et utiliserons vos données personnelles dans la mesure
              nécessaire pour respecter nos obligations légales (par exemple, si la loi nous oblige à les
              conserver), régler des différends et faire respecter nos ententes et politiques.</p>
            <p>Concrètement, cela signifie ce qui suit :</p>
            <ul>
              <li>Les <strong>renseignements de compte</strong> — tels que votre nom, votre adresse courriel et les
                équipes dont vous faites partie — sont conservés tant que votre compte existe. La suppression de
                votre compte les supprime, sauf lorsque la loi nous oblige à en conserver une partie.</li>
              <li>Les <strong>séquences enregistrées avec BoothPilot Replay</strong> ne sont pas détenues par nous.
                Elles sont conservées dans le compte Google Drive lié par l'administrateur de votre équipe, aussi
                longtemps que votre équipe les y garde. Les renseignements que nous détenons à leur sujet —
                l'événement, les étiquettes et le membre qui les a enregistrées — sont conservés jusqu'à la
                suppression de la séquence ou du compte.</li>
              <li>Les <strong>enregistrements livrés à un outil vidéo externe</strong> que vous avez connecté, tel que
                votre chaîne YouTube ou Hudl, sont conservés par nous pendant au plus <strong>7 jours</strong> suivant
                la livraison. Ce que cet outil externe conserve par la suite est régi par vos ententes avec
                celui-ci.</li>
              <li>Les <strong>enregistrements que nous hébergeons</strong> aux fins de lecture au moyen du Service
                sont conservés pendant <strong>180 jours</strong> par défaut. Une période de conservation différente
                peut être convenue par écrit avec votre équipe, auquel cas cette période s'applique.</li>
            </ul>
            <p>Les copies de sauvegarde peuvent contenir du contenu supprimé pendant une brève période par la suite,
              avant d'expirer dans le cours normal des activités.</p>
            <p>La Société conservera également les données d'utilisation à des fins d'analyse interne. Ces
              données sont généralement conservées pour une période plus courte, sauf lorsqu'elles servent à
              renforcer la sécurité ou à améliorer le fonctionnement du Service, ou lorsque la loi nous oblige
              à les conserver plus longtemps.</p>

            <h3>Transfert de vos données personnelles</h3>
            <p>Vos renseignements, y compris vos données personnelles, sont traités dans les bureaux
              d'exploitation de la Société et dans tout autre lieu où se trouvent les parties participant au
              traitement. Nous faisons appel à des fournisseurs tiers pour la technologie nécessaire au
              fonctionnement du Service, et ces fournisseurs peuvent recueillir et stocker vos données pour
              notre compte. Ces renseignements peuvent donc être transférés vers des ordinateurs situés à
              l'extérieur de votre province, de votre pays ou de toute autre autorité gouvernementale où les
              lois sur la protection des données peuvent différer de celles de votre territoire, et y être
              conservés.</p>
            <p>Votre acceptation de la présente politique de confidentialité, suivie de la transmission de tels
              renseignements, constitue votre consentement à ce transfert.</p>
            <p>La Société prendra toutes les mesures raisonnablement nécessaires pour que vos données soient
              traitées de façon sécuritaire et conformément à la présente politique, et aucun transfert de vos
              données personnelles ne sera effectué vers une organisation ou un pays sans que des contrôles
              adéquats soient en place, notamment quant à la sécurité de vos données et de vos autres
              renseignements personnels.</p>

            <h3>Divulgation de vos données personnelles</h3>
            <h4>Opérations commerciales</h4>
            <p>Si la Société participe à une fusion, à une acquisition ou à une vente d'actifs, vos données
              personnelles pourraient être transférées. Nous vous en aviserons avant que vos données
              personnelles ne soient transférées et ne deviennent assujetties à une politique de
              confidentialité différente.</p>
            <h4>Application de la loi</h4>
            <p>Dans certaines circonstances, la Société peut être tenue de divulguer vos données personnelles
              si la loi l'exige ou en réponse à des demandes valides d'autorités publiques (par exemple, un
              tribunal ou un organisme gouvernemental).</p>
            <h4>Autres exigences légales</h4>
            <p>La Société peut divulguer vos données personnelles si elle estime de bonne foi qu'une telle
              mesure est nécessaire pour :</p>
            <ul>
              <li>respecter une obligation légale;</li>
              <li>protéger et défendre les droits ou les biens de la Société;</li>
              <li>prévenir ou examiner d'éventuels actes répréhensibles liés au Service;</li>
              <li>protéger la sécurité des utilisateurs du Service ou du public;</li>
              <li>se prémunir contre toute responsabilité légale.</li>
            </ul>

            <h3>Sécurité de vos données personnelles</h3>
            <p>La sécurité de vos données personnelles nous importe, mais rappelez-vous qu'aucune méthode de
              transmission sur Internet ni aucune méthode de stockage électronique n'est sûre à 100 %. Bien que
              nous nous efforcions d'utiliser des moyens commercialement acceptables pour protéger vos données
              personnelles, nous ne pouvons en garantir la sécurité absolue.</p>

            <h2>Vie privée des enfants</h2>
            <p>Notre Service ne s'adresse pas aux personnes de moins de 13 ans. Nous ne recueillons pas
              sciemment de renseignements permettant d'identifier une personne de moins de 13 ans. Si vous êtes
              un parent ou un tuteur et que vous savez que votre enfant nous a fourni des données personnelles,
              veuillez communiquer avec nous. Si nous apprenons que nous avons recueilli des données
              personnelles auprès d'une personne de moins de 13 ans sans vérification du consentement parental,
              nous prenons les mesures nécessaires pour supprimer ces renseignements.</p>

            <h2>Services de tiers</h2>
            <p>Les données recueillies sur le site d'un client sont gérées et contrôlées par ce client
              (l'exploitant). Notre société n'agit qu'à titre de responsable du traitement des données
              recueillies. Il incombe aux exploitants d'informer les joueurs, les entraîneurs ou toute autre
              personne et, au besoin, d'obtenir leur consentement à être filmés et à la diffusion de la vidéo
              en direct sur Internet.</p>
            <p>Notre Service ne diffuse aucune publicité, ni sur le Site Web ni dans le contenu transmis à des
              services tiers.</p>
            <p>Notre Service contient des liens et des intégrations vers d'autres sites Web que nous
              n'exploitons pas. Si vous cliquez sur un lien vers un site tiers, vous serez dirigé vers ce site.
              Nous vous recommandons fortement de consulter la politique de confidentialité de chaque site que
              vous visitez.</p>
            <p>Les deux produits reposent sur une infrastructure commune fournie par :</p>
            <ul>
              <li><strong>Firebase (Google)</strong> — authentification, base de données et fonctions
                infonuagiques. Voir la
                <a href="https://firebase.google.com/support/privacy" target="_blank">politique de
                  confidentialité de Firebase</a>.</li>
            </ul>
            <p>Chaque produit possède aussi ses propres intégrations facultatives, présentées sous
              <a [routerLink]="[]" fragment="product-disclosures-fr" queryParamsHandling="preserve">Renseignements
                propres à chaque produit</a> ci-dessous.</p>
            <p>Nous n'exerçons aucun contrôle sur le contenu, les politiques de confidentialité ou les
              pratiques des sites ou services de tiers, et nous n'en assumons aucune responsabilité.</p>

            <h2 id="product-disclosures-fr">Renseignements propres à chaque produit</h2>
            <p>Les sections suivantes décrivent le traitement propre à un seul produit. Tout ce qui précède
              demeure applicable.</p>

            <h3 id="boothpilot-fr">BoothPilot (Web)</h3>
            <p>Les intégrations ci-dessous sont facultatives et visent l'application Web uniquement. Elles ne
              sont pas offertes dans BoothPilot Replay et n'y sont pas utilisées.</p>

            <h4>YouTube</h4>
            <p>Si vous choisissez de connecter une chaîne YouTube, nous demandons la portée
              <strong>https://www.googleapis.com/auth/youtube.force-ssl</strong>. Nous l'utilisons uniquement
              pour créer et gérer les diffusions en direct que vous planifiez dans le Service : créer des
              diffusions et des flux, les associer à votre chaîne et en consulter l'état.</p>
            <p>Nous conservons les jetons d'accès et d'actualisation qui en résultent, les liens publics vers
              les diffusions ainsi que les clés de diffusion. Ces données sont conservées de façon sécuritaire
              avec des règles de contrôle d'accès et sont actualisées, aux fins de validité et d'autorisation,
              au moins tous les 7 jours.</p>
            <p>En utilisant cette intégration, vous acceptez les
              <a href="https://www.youtube.com/t/terms" target="_blank">conditions d'utilisation de YouTube</a>
              et la
              <a href="http://www.google.com/policies/privacy" target="_blank">politique de confidentialité de
                Google</a>.</p>
            <p>Vous pouvez révoquer cet accès à tout moment au moyen du bouton de révocation dans l'application
              ou à partir de la page des paramètres de sécurité de Google à
              <a href="https://myaccount.google.com/permissions" target="_blank">https://myaccount.google.com/permissions</a>.
              Lorsque l'accès est révoqué, toutes les données associées à votre compte YouTube sont supprimées
              de l'application.</p>

            <h4>Hudl</h4>
            <p>Les événements captés peuvent être téléversés vers votre équipe Hudl. Vous pouvez consulter la
              <a href="https://www.hudl.com/privacy" target="_blank">politique de confidentialité de Hudl</a>.</p>

            <h3 id="replay-fr">BoothPilot Replay (mobile)</h3>
            <h4>Données de compte et d'équipe</h4>
            <p>Lorsque vous créez un compte, nous recueillons votre adresse courriel, votre nom d'affichage et
              les données provenant de votre fournisseur d'authentification si vous vous connectez avec Google
              ou avec Apple plutôt qu'avec une adresse courriel et un mot de passe. Au fil de votre utilisation
              de l'Application, nous enregistrons vos appartenances aux équipes et votre rôle au sein de chaque
              équipe, les métadonnées d'événement telles que les noms, les dates et les
              étiquettes, ainsi que les métadonnées de séquence telles que les horodatages, les étiquettes de
              jeu et les références de fichiers.</p>

            <h4>Données vidéo et images</h4>
            <p><strong>Nous ne conservons pas vos enregistrements vidéo ni vos vignettes sur nos
              serveurs.</strong> Les fichiers vidéo et les vignettes sont téléversés directement de votre
              Appareil vers le compte Google Drive lié à votre équipe. Nous ne conservons dans notre base de
              données que les identifiants de fichiers Google Drive, afin de permettre la lecture et la
              synchronisation. Les fichiers multimédias eux-mêmes demeurent dans le compte Google Drive
              contrôlé par l'administrateur de votre équipe.</p>
            <p>Des copies locales des vidéos peuvent être mises en cache sur votre Appareil pour la lecture
              hors ligne. Elles sont supprimées lorsque vous désinstallez l'Application.</p>

            <h4>Accès à Google Drive et utilisation limitée</h4>
            <p>Pour stocker les séquences de votre équipe, un administrateur d'équipe lie un compte Google
              Drive. Nous demandons une seule portée :</p>
            <ul>
              <li><strong>https://www.googleapis.com/auth/drive.file</strong> — permet à l'Application de créer
                des fichiers dans Google Drive et d'accéder uniquement aux fichiers qu'elle a créés. Elle
                n'accorde aucun accès aux autres fichiers de votre Drive. Nous l'utilisons pour téléverser les
                séquences et les vignettes, les relire aux fins de lecture et les supprimer lorsque vous
                supprimez une séquence dans l'Application.</li>
            </ul>
            <p>Notre utilisation et notre transfert des renseignements reçus des API Google respectent la
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank">politique
                relative aux données utilisateur des services d'API Google</a>, y compris ses exigences
              d'utilisation limitée. Plus précisément, les données obtenues au moyen des API Google servent
              uniquement à fournir et à améliorer les fonctionnalités destinées à l'utilisateur décrites
              ci-dessus; elles ne sont utilisées à aucune fin publicitaire; elles ne sont ni vendues ni
              transférées à des tiers, sauf dans la mesure nécessaire à la prestation du Service, pour se
              conformer à la loi applicable ou dans le cadre d'une fusion ou d'une acquisition; et elles ne
              sont pas consultées par des personnes, sauf avec votre consentement explicite, pour répondre à
              une demande d'assistance, à des fins de sécurité comme l'examen d'un usage abusif, ou lorsque la
              loi l'exige.</p>
            <p>Un administrateur d'équipe peut dissocier le compte Drive lié à tout moment depuis l'écran des
              paramètres d'équipe, et vous pouvez révoquer l'accès de l'Application à
              <a href="https://myaccount.google.com/permissions" target="_blank">https://myaccount.google.com/permissions</a>.</p>

            <h4>Demandes d'assistance et commentaires</h4>
            <p>Si vous nous envoyez une demande d'assistance ou des commentaires depuis l'Application, nous
              conservons le message que vous rédigez ainsi que votre nom, votre adresse courriel, l'équipe que vous
              utilisiez à ce moment et le système utilisé (iOS ou Android), afin de pouvoir vous répondre et
              reproduire le problème. Les demandes d'assistance et les commentaires sont conservés séparément des
              données de jeu de votre équipe et servent uniquement à vous répondre et à améliorer l'Application.</p>
            <p>L'Application conserve également sur votre Appareil un court journal technique des téléversements
              récents. Ce journal n'est jamais transmis automatiquement. Il n'est partagé que si vous choisissez de
              le partager, au moyen des options de partage de votre Appareil, et vous en choisissez la destination.
              Les jetons d'accès, les adresses Web et les chemins de fichiers en sont retirés avant qu'il ne vous
              soit présenté.</p>

            <h4>Identifiant d'appareil</h4>
            <p>L'Application génère un identifiant aléatoire pour chaque installation et le conserve sur votre
              Appareil. Il est enregistré, avec un nom d'appareil que vous pouvez modifier, lorsque votre appareil
              détient le contrôle de l'enregistrement ou de la saisie de données, afin que les membres de votre
              équipe voient quel appareil enregistre. Il n'est dérivé d'aucun identifiant matériel ou publicitaire,
              n'est communiqué à personne et ne sert pas à vous suivre. Il est supprimé lorsque vous désinstallez
              l'Application.</p>

            <h4>Autorisations de l'appareil</h4>
            <p>L'Application demande l'accès à votre <strong>caméra</strong>, pour filmer, et à votre
              <strong>microphone</strong>, pour capter le son accompagnant ces enregistrements. Ces
              autorisations ne sont demandées qu'au moment où la fonction est utilisée et peuvent être
              révoquées à tout moment dans les paramètres de votre appareil.</p>

            <h4>Lieu d'hébergement des données</h4>
            <p>Lorsque vous créez une équipe, vous choisissez une région de données, actuellement les
              États-Unis ou le Canada. Les données de votre équipe sont stockées dans la base de données de
              cette région. Les données liées au compte sont stockées aux États-Unis.</p>

            <h4>Suppression de votre compte</h4>
            <p>Vous pouvez supprimer votre compte à tout moment depuis l'Application. Cette opération vous
              retire de toutes les équipes, supprime votre profil d'utilisateur de toutes les bases de données
              régionales et supprime votre compte d'authentification. Les fichiers vidéo conservés dans Google
              Drive ne sont pas touchés, car ils sont contrôlés par le compte Drive de votre équipe et non par
              nous.</p>

            <h4>Intégrations facultatives</h4>
            <p>En plus de l'infrastructure Firebase commune décrite ci-dessus, l'Application utilise :</p>
            <ul>
              <li><strong>API Google Drive</strong> — stockage des vidéos et des vignettes, comme décrit
                ci-dessus. Voir la
                <a href="https://policies.google.com/privacy" target="_blank">politique de confidentialité de
                  Google</a>.</li>
              <li><strong>Connexion avec Google</strong> — authentification facultative. Lorsque vous l'utilisez,
                nous recevons votre nom, votre adresse courriel et votre photo de profil de votre compte Google, et
                rien d'autre; nous ne demandons l'accès à aucun autre service Google par ce moyen. Voir la
                <a href="https://policies.google.com/privacy" target="_blank">politique de confidentialité de
                  Google</a>.</li>
              <li><strong>Connexion avec Apple</strong> — authentification facultative sur les appareils iOS.
                Voir la <a href="https://www.apple.com/legal/privacy/" target="_blank">politique de
                  confidentialité d'Apple</a>.</li>
            </ul>

            <h2>Modifications de la présente politique de confidentialité</h2>
            <p>Nous pouvons mettre à jour notre politique de confidentialité de temps à autre. Nous vous
              informerons de toute modification en publiant la nouvelle politique sur cette page.</p>
            <p>Nous vous en aviserons par courriel ou par un avis bien visible dans notre Service avant que la
              modification ne prenne effet, et nous mettrons à jour la date de « dernière mise à jour » au haut
              de la présente politique.</p>
            <p>Nous vous invitons à consulter périodiquement la présente politique de confidentialité afin d'en
              prendre connaissance des modifications. Les modifications apportées à la présente politique
              entrent en vigueur au moment de leur publication sur cette page.</p>

            <h2>Nous joindre</h2>
            <p>Pour toute question concernant la présente politique de confidentialité ou nos pratiques en
              matière de vie privée, vous pouvez communiquer avec nous :</p>
            <ul>
              <li>Par courriel : hostmaster&#64;boothpilot.com</li>
            </ul>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>